import React, { useMemo, useEffect, useState } from 'react';
import {
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, ButtonGroup, Tooltip, IconButton,
    Button, Snackbar, Alert, DialogContentText, Autocomplete,
} from '@mui/material';
import MaterialReactTable from 'material-react-table';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import colors from "assets/mavric-theme/base/colors.js";
import typography from "assets/mavric-theme/base/typography";
import borders from "assets/mavric-theme/base/borders";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import { deleteProbe } from 'apis/ProbeInventoryApis';

const { primary, secondary, text, tableRow, transparent, white } = colors;
const { size } = typography;
const { borderRadius } = borders;
const ITEM_HEIGHT = 48;

export default function ProbeInventoryTable(props) {

    const columns = useMemo(
        () => [
            {
                accessorKey: 'probe_type',
                header: 'Probe Type',
                size: 200
              },
              {
                accessorKey: 'serial_number',
                header: 'Serial Number',
                size: 120
              },
              {
                accessorKey: 'model_name',
                header: 'Model Name',
                size: 120
              },
              {
                accessorKey: 'mac_address',
                header: 'MAC Address',
                size: 120
              },
              {
                accessorKey: 'software_version',
                header: 'Software Version',
                size: 120
              },
              {
                accessorKey: 'main_board_rev',
                header: 'Main Board Rev',
                size: 120
              },
              {
                accessorKey: 'customer_info',
                header: 'Customer Info',
                size: 120
              },
              {
                accessorKey: 'so_number',
                header: 'SO Number',
                size: 100
              },
              {
                accessorKey: 'last_modified',
                header: 'Last Modified',
                // size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value ? new Date(value).toLocaleString() : '-';
                },
              },
        ],
        [],
    );

    const [probeData, setProbeData] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [selectedProbes, setSelectedProbes ] = useState([]);
    const [muiTableKey, setMuiTableKey] = useState(0);

    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [pageAlertType, setPageAlertType] = useState("error");
    const [alertState, setAlertState] = useState({ openAlert: false, alertSeverity: "error", alertMessage: "", vertical: 'bottom', horizontal: 'left' });
    const { openAlert, alertSeverity, alertMessage, vertical, horizontal } = alertState;
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteMessageConfirm, setDeleteMessageConfirm] = useState("");
    const [deleteMessageConfirmTitle, setDeleteMessageConfirmTitle] = useState("");
    const [deleteAlertMsg, setDeleteAlertMsg] = useState("");

    useEffect(() => {
      if(props.probeInventoryData.length > 0) setProbeData(props.probeInventoryData);
    }, [props.probeInventoryData]);

    // console.log("probeData::", props.probeInventoryData);

    // Show 500 or all records
    const displayedData = showAll ? probeData : probeData.slice(0, 500);
    // console.log("displayedData::", displayedData);

    // const handleDeleteProbeInventory = () => {
    //   console.log(selectedProbes);
    //   setSelectedProbes([]);
    // }

    const handleEditProbeInventory = (row) => {
        // console.log(row);
        props.setFormData(row);
        props.setIsEditMode(true);
        props.setOpenAddProbeDialog(true);
      }

    const resetMyFilters = () => {
      setMuiTableKey(muiTableKey + 1);
    };

    const handleDeleteProbeInventory = () => {
        let strMsg = "Are you sure you want to delete?";
        setDeleteMessageConfirm(strMsg);
        setDeleteMessageConfirmTitle("Delete selected");
        setConfirmDeleteOpen(true);
    };
  
    const handleDeleteYes = () => {
        setConfirmDeleteOpen(false);
        console.log(selectedProbes);
        if (selectedProbes.length > 0) {
            deleteProbes(selectedProbes);
    }
    resetMyFilters();
    setSelectedProbes([]);
    }
    
    const handleDeleteNo = () => {
    resetMyFilters();
    setSelectedProbes([]);
    setConfirmDeleteOpen(false);
    };
    
    const handleConfirmClose = () => {
    setConfirmDeleteOpen(false);
    setSelectedProbes([]);
    };

    const handleDeleteAlertClose = (event, reason) => {

    if (reason === 'clickaway') {
    return;
    }
    setAlertState({ ...alertState, openAlert: false });
    setShowDeleteAlert(false);
    }

    const deleteProbes = async (selectedProbes) => {
        let errorList = [];
        let deletedUsers = [];
        
        for (let idx = 0; idx < selectedProbes.length; idx++) {
            let probeInfo = selectedProbes[idx];
        
            try {
            const result = await deleteProbe(probeInfo.serial_number);
        
            if (result.code !== 200) {
                probeInfo['error_msg'] = JSON.stringify(result);
                errorList.push(probeInfo);
            } else {
                deletedUsers.push(probeInfo);
            }
            } catch (error) {
            probeInfo['error_msg'] = error;
            errorList.push(probeInfo);
            }
        }
    
        // Call refresh only once
        props.getProbeInventoryData();
    
        setShowDeleteAlert(true);
        if (errorList.length > 0) {
            console.error("Error: Probe List :", errorList);
            setPageAlertType("error");
            setDeleteAlertMsg(`Error: ${errorList.length} probe(s) were not deleted`);
        } else {
            setPageAlertType("success");
            setDeleteAlertMsg(`Success : ${deletedUsers.length} probe(s) were deleted`);
        }
    };

    return (
        <div style={{ maxHeight: '500px', overflow: 'auto' }}>
          {!showAll && (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setShowAll(true)}
                    sx={{ m:1 }}
                  >
                    Show All
                  </Button>
                </>
              )}
            {
            // probeData.length === 0 ? <Typography sx={{ display: 'flex', justifyContent: 'center', color: '#8080807a', }}>No Data Found</Typography> :
                <MaterialReactTable
                key={muiTableKey}
                columns={columns}
                data={displayedData}
                state={{ isLoading: props.loading }}
                enableStickyHeader={true}
                muiTableToolbarAlertBannerProps={{ sx: { display: 'none' } }}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', backgroundColor: primary.main } }}
                    muiTableBodyRowProps={({ row }) => ({
                        hover: true,
                        sx: {
                            color: text.main,
                            align: 'center',
                            '&:nth-of-type(odd)': {
                                backgroundColor: tableRow.dark,
                                border: 0,
                            },
                            '&:nth-of-type(even)': {
                                background: tableRow.light,
                            },
                            // hide last border
                            '&:last-child td, &:last-child th': {
                                border: 0,
                            },
                            '&:hover td': {
                                background: tableRow.hover,
                            },
                        },
                    })}
                    //muiTableBodyProps={{sx:{boxShadow: 'none'}}}
                    muiTableBodyCellProps={{ sx: { border: 'none', color: text.main, textAlign: 'center', boxShadow: 'none', padding: '4px 8px', } }}
                    muiTableProps={{ sx: { backgroundColor: primary.main, color: text.main, boxShadow: 'none' } }}
                    // muiTableToolbarTopProps={{ sx: {backgroundColor: primary.main, boxShadow: 'none', minHeight: 0} }}
                    // muiTableToolbarBottomProps={{ sx: {backgroundColor: primary.main, boxShadow: 'none'} }}
                    muiTopToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none', minHeight: 0 } }}
                    muiBottomToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none' } }}
                    muiTableHeadRowProps={{ sx: { backgroundColor: primary.main, color: text.main, textAlign: 'center' }, }}
                    muiTableHeadCellProps={{ sx: { borderBottom: 0, verticalAlign: 'none', boxShadow: 'none', textAlign: 'center', color: text.main } }}
                    // enableToolbarTop={false}
                    enableColumnFilters={false}
                    enableRowSelection={true}
                    // enableRowSelection={isAccounting}
                    enableRowVirtualization
                    enableColumnActions={false}
                    enableColumnOrdering={false}
                    enablePinning={false}
                    enableSelectAll={false}
                    enableClickToCopy={false}

                    // enableSorting={false}
                    enableRowActions
                    positionPagination="bottom"
                    enableToolbarInternalActions={false}
                    initialState={{
                        density: 'compact',
                        showColumnFilters: true,
                        boxShadow: 'none',
                        pagination: { pageSize: 100, pageIndex: 0, pageCount: -1, },
                        // sorting: [
                        //     {
                        //       id: 'account_activation_updated_on', //sort by account_activation_updated_on by default on page load
                        //       desc: true,
                        //     }
                        //   ],
                    }}
                    positionActionsColumn="last"
                    positionToolbarAlertBanner={'none'}
                    muiTablePaginationProps={{
                        // rowsPerPageOptions: [3, 5, 10, 15, 20],
                        sx: { color: text.main, '& p': { marginBottom: 0 }, '& .MuiSvgIcon-root': { color: text.main, },}
                    }}
                    // muiSearchTextFieldProps={{
                    //     // placeholder: `Search ${data.length} rows`,
                    //     sx: { minWidth: '300px' },
                    //     variant: 'outlined',
                    // }}

                    muiTableContainerProps={{
                        sx: {
                            padding: '0 !important',
                        }
                    }}

                    renderTopToolbar={({ table }) => {
                        const handleDeleteProbes = () => {
                            handleDeleteProbeInventory();
                            table.getSelectedRowModel().flatRows.map((row) => {
                                setSelectedProbes(current => [...current, row.original]);
                            });
                        };

                        return (
                            <>
                              <div style={{ backgroundColor: primary.main, }}>
                                  <Button
                                      color="secondary"
                                      disabled={table.getSelectedRowModel().flatRows.length === 0}
                                      onClick={handleDeleteProbes}
                                      variant="contained"
                                      size="small"
                                      sx={{ mr: 1, ml: 1 }}
                                  >
                                      Delete Probe Inventory
                                  </Button>
                                  <Button onClick={resetMyFilters} mr={2} variant="contained"
                                      sx={{
                                          '&:hover': {
                                              backgroundColor: secondary.main,
                                          },
                                      }} color="secondary" size="small" startIcon={<RefreshIcon sx={{ backgroundColor: secondary.main }} size="small" />}>
                                      Reset Filters
                                  </Button>
                              </div>

                              <Dialog open={confirmDeleteOpen} onClose={handleConfirmClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth='md'
                                    sx={{ '& .MuiDialog-paper': {
                                        backgroundColor: primary.main,
                                        }
                                    }}
                                    >
                                    <DialogTitle id="alert-dialog-title">
                                        <Typography fontSize={size.lg} color={secondary.main} sx={{ fontWeight: 600,}}>{deleteMessageConfirmTitle}</Typography>
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText id="alert-dialog-description">{deleteMessageConfirm}</DialogContentText>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button variant="contained" color="secondary" startIcon={<CancelIcon />}
                                        onClick={handleDeleteNo}>No</Button>
                
                                        <Button variant="contained" color="secondary" startIcon={<DeleteIcon />}
                                        onClick={handleDeleteYes} autoFocus>Yes</Button>
                                    </DialogActions>
                                </Dialog>
                                <Snackbar
                                    anchorOrigin={{ vertical, horizontal }}
                                    open={showDeleteAlert}
                                    autoHideDuration={6000}
                                    key={vertical + horizontal}
                                    onClose={handleDeleteAlertClose}
                                >
                                    <Alert elevation={6} variant="filled" onClose={handleDeleteAlertClose}
                                        severity={pageAlertType || "error"} sx={{ "& .MuiAlert-message" :{ color: white.main, fontSize: size.sm}, marginBottom: 4 }}
                                        >
                                    {deleteAlertMsg}
                                    </Alert>
                                </Snackbar>
                            </>
                        );
                    }}
                    renderRowActions={({ row }) => (
                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.5rem' }}>
                           <ButtonGroup variant="text" color="primary" size="small" aria-label="text primary button group">
                              {/* <Tooltip title="View" aria-label="Apply">
                                  <IconButton sx={{
                                      border: 1, borderRadius: 2, backgroundColor: secondary.main, borderColor: secondary.main, width: 30, height: 30,
                                      '&:hover': {
                                          backgroundColor: secondary.main,
                                      },
                                  }} color="text" size="small"
                                      onClick={() => {
                                          handleClickOpenBilledCustomerDetailView(row.original)
                                      }}
                                  >
                                      <VisibilityIcon style={{ backgroundColor: secondary.main }} size="small" />
                                  </IconButton>
                              </Tooltip>
                              &nbsp;&nbsp; */}
                              <Tooltip title="Edit" aria-label="Rename Matrix">
                                  <IconButton sx={{
                                      border: 1, borderRadius: 2, backgroundColor: secondary.main, borderColor: secondary.main,
                                      width: 30, height: 30,
                                      '&:hover': {
                                          backgroundColor: secondary.main,
                                      },
                                  }} color="text" size="small"
                                      onClick={() => {
                                        handleEditProbeInventory(row.original);
                                        console.log("Edit::",row.original);
                                          // handleBilledCustClickOpen(row.original)
                                      }}
                                  >
                                      <EditIcon style={{ backgroundColor: secondary.main }} size="small" />
                                  </IconButton>
                              </Tooltip>
                          </ButtonGroup>
                        </div>
                    )}
                />
            }
        </div>
      );

}